import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, DollarSign, Save, Edit2, FileText, Download, AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";

function fmt(n) {
  return (n ?? 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

function TableSection({ title, rows, keyA, keyB, format = "currency" }) {
  const fmtVal = v => format === "currency" ? fmt(v) : v;
  return (
    <div>
      <h4 className="font-semibold text-slate-800 mb-2">{title}</h4>
      <table className="w-full text-sm border rounded-lg overflow-hidden">
        <thead className="bg-slate-50">
          <tr>
            <th className="text-left px-3 py-2 text-slate-600">Concepto</th>
            <th className="text-right px-3 py-2 text-slate-600">Importe</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t">
              <td className="px-3 py-1.5 text-slate-700">{r[keyA]}</td>
              <td className="px-3 py-1.5 text-right font-mono">{fmtVal(r[keyB])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function OpsRRHHNominaRun() {
  const { runId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [run, setRun] = useState(null);
  const [period, setPeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editForm, setEditForm] = useState({
    percepciones: [],
    deducciones: [],
    otros_pagos: [],
    payment_method: "transferencia",
    payment_reference: "",
    edit_notes: "",
  });

  const isHR = ["hr_admin", "corporate_admin"].includes(user?.role);

  const loadRun = useCallback(async () => {
    try {
      const r = await api.get(`/ops/payroll/runs/${runId}`);
      setRun(r.data);
      setEditForm({
        percepciones: JSON.parse(JSON.stringify(r.data.percepciones || [])),
        deducciones: JSON.parse(JSON.stringify(r.data.deducciones || [])),
        otros_pagos: JSON.parse(JSON.stringify(r.data.otros_pagos || [])),
        payment_method: r.data.payment_method || "transferencia",
        payment_reference: r.data.payment_reference || "",
        edit_notes: "",
      });
      // load period
      const p = await api.get(`/ops/payroll/periods/${r.data.payroll_period_id}`);
      setPeriod(p.data);
    } catch {
      toast.error("Error al cargar el calculo");
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => { loadRun(); }, [loadRun]);

  const canEdit = isHR && period?.status === "calculado";

  const handleSave = async () => {
    if (editForm.edit_notes.trim().length < 10) {
      toast.error("El motivo de edicion debe tener al menos 10 caracteres");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put(`/ops/payroll/runs/${runId}`, editForm);
      setRun(r.data);
      setEditing(false);
      toast.success("Calculo actualizado correctamente");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar cambios");
    } finally {
      setSaving(false);
    }
  };

  const setPercepciones = (idx, field, val) => {
    setEditForm(f => {
      const arr = [...f.percepciones];
      arr[idx] = { ...arr[idx], [field]: field === "importe_gravado" || field === "importe_exento" ? parseFloat(val) || 0 : val };
      return { ...f, percepciones: arr };
    });
  };

  const setDeducciones = (idx, field, val) => {
    setEditForm(f => {
      const arr = [...f.deducciones];
      arr[idx] = { ...arr[idx], [field]: field === "importe" ? parseFloat(val) || 0 : val };
      return { ...f, deducciones: arr };
    });
  };

  if (loading) return <div className="p-6 text-slate-400 text-center">Cargando calculo...</div>;
  if (!run) return <div className="p-6 text-slate-400">Calculo no encontrado</div>;

  const periodId = run.payroll_period_id;
  const timbrado = run.cfdi_n_status === "timbrado";

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/ops/rrhh/nomina/${periodId}`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#FFDF00]" />
              Recibo de Nomina
            </h1>
            <p className="text-sm text-slate-500" data-testid="run-employee-name">{run.employee_name} · {period?.period_number}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {run.cfdi_n_xml_url && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/ops/payroll/runs/${runId}/cfdi-xml`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md hover:bg-slate-50"
              data-testid="btn-download-xml"
            >
              <FileText className="w-4 h-4" /> XML
            </a>
          )}
          {run.cfdi_n_pdf_url && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/ops/payroll/runs/${runId}/cfdi-pdf`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md hover:bg-slate-50"
              data-testid="btn-download-pdf"
            >
              <FileText className="w-4 h-4" /> Recibo
            </a>
          )}
          {canEdit && !editing && (
            <Button
              variant="outline"
              onClick={() => setEditing(true)}
              data-testid="btn-edit"
            >
              <Edit2 className="w-4 h-4 mr-1" /> Editar
            </Button>
          )}
        </div>
      </div>

      {/* Info del empleado */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "No. Empleado", value: run.employee_number },
          { label: "Dias trabajados", value: `${run.days_worked} / ${run.days_in_period}` },
          { label: "KPI %", value: `${run.kpi_achievement_pct?.toFixed(1) ?? "-"}%` },
          { label: "Bono KPI", value: fmt(run.kpi_bonus_amount) },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <p className="text-xs text-slate-500">{label}</p>
            <p className="font-semibold text-slate-900 mt-0.5">{value}</p>
          </div>
        ))}
      </div>

      {run.kpi_bonus_note && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800" data-testid="kpi-bonus-note">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span><strong>Bono KPI en $0:</strong> {run.kpi_bonus_note}</span>
        </div>
      )}

      {/* Totales destacados */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-xs text-slate-500">Total percepciones</p>
          <p className="text-xl font-bold text-slate-900 font-mono mt-0.5">{fmt(run.total_percepciones)}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <p className="text-xs text-slate-500">Total deducciones</p>
          <p className="text-xl font-bold text-red-600 font-mono mt-0.5">-{fmt(run.total_deducciones)}</p>
        </div>
        <div className="bg-[#FFDF00]/10 rounded-xl border border-yellow-200 p-4 shadow-sm">
          <p className="text-xs text-slate-600">Neto a pagar</p>
          <p className="text-xl font-bold text-emerald-700 font-mono mt-0.5" data-testid="neto-pagar">{fmt(run.neto_pagar)}</p>
        </div>
      </div>

      {/* CFDI status */}
      {run.cfdi_n_status !== "pending" && (
        <div className={`rounded-xl border p-4 flex items-start gap-3 ${timbrado ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
          <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${timbrado ? "text-green-600" : "text-red-500"}`} />
          <div>
            <p className={`font-semibold ${timbrado ? "text-green-800" : "text-red-700"}`}>
              CFDI {run.cfdi_n_status === "timbrado" ? "timbrado" : run.cfdi_n_status}
            </p>
            {run.cfdi_n_uuid && <p className="text-sm font-mono text-slate-600 mt-0.5">{run.cfdi_n_uuid}</p>}
            {run.cfdi_n_error_message && <p className="text-sm text-red-600 mt-0.5">{run.cfdi_n_error_message}</p>}
          </div>
        </div>
      )}

      {/* Desglose */}
      {!editing ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
            <TableSection
              title="Percepciones"
              rows={run.percepciones || []}
              keyA="concepto"
              keyB="importe_gravado"
            />
            {(run.otros_pagos?.length > 0) && (
              <TableSection title="Otros pagos" rows={run.otros_pagos} keyA="concepto" keyB="importe" />
            )}
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
            <TableSection title="Deducciones" rows={run.deducciones || []} keyA="concepto" keyB="importe" />
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">Costos patronales (informativo)</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-slate-600"><span>IMSS patron</span><span className="font-mono">{fmt(run.patron_imss)}</span></div>
                <div className="flex justify-between text-slate-600"><span>INFONAVIT patron</span><span className="font-mono">{fmt(run.patron_infonavit)}</span></div>
                <div className="flex justify-between text-slate-600"><span>SAR</span><span className="font-mono">{fmt(run.patron_sar)}</span></div>
                <div className="flex justify-between text-slate-600"><span>ISN</span><span className="font-mono">{fmt(run.patron_isn)}</span></div>
                <div className="flex justify-between font-semibold text-slate-700 border-t pt-1">
                  <span>Total costos patron</span>
                  <span className="font-mono">{fmt((run.patron_imss || 0) + (run.patron_infonavit || 0) + (run.patron_sar || 0) + (run.patron_isn || 0))}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-amber-200 p-5 shadow-sm space-y-5">
          <div className="flex items-center gap-2 text-amber-700">
            <Edit2 className="w-4 h-4" />
            <span className="font-semibold text-sm">Modo edicion — solo permitido antes de autorizar</span>
          </div>

          {/* Editar percepciones */}
          <div>
            <h4 className="font-semibold text-slate-700 mb-2">Percepciones</h4>
            {editForm.percepciones.map((p, i) => (
              <div key={i} className="flex gap-2 items-center mb-2">
                <input
                  className="flex-1 border rounded px-2 py-1.5 text-sm"
                  value={p.concepto}
                  onChange={e => setPercepciones(i, "concepto", e.target.value)}
                />
                <input
                  type="number"
                  className="w-32 border rounded px-2 py-1.5 text-sm text-right"
                  value={p.importe_gravado}
                  onChange={e => setPercepciones(i, "importe_gravado", e.target.value)}
                />
              </div>
            ))}
          </div>

          {/* Editar deducciones */}
          <div>
            <h4 className="font-semibold text-slate-700 mb-2">Deducciones</h4>
            {editForm.deducciones.map((d, i) => (
              <div key={i} className="flex gap-2 items-center mb-2">
                <input
                  className="flex-1 border rounded px-2 py-1.5 text-sm"
                  value={d.concepto}
                  onChange={e => setDeducciones(i, "concepto", e.target.value)}
                />
                <input
                  type="number"
                  className="w-32 border rounded px-2 py-1.5 text-sm text-right"
                  value={d.importe}
                  onChange={e => setDeducciones(i, "importe", e.target.value)}
                />
              </div>
            ))}
          </div>

          {/* Metodo de pago */}
          <div className="flex gap-4 items-center">
            <div>
              <label className="text-sm font-semibold text-slate-700">Metodo de pago</label>
              <select
                className="block mt-1 border rounded px-2 py-1.5 text-sm"
                value={editForm.payment_method}
                onChange={e => setEditForm(f => ({ ...f, payment_method: e.target.value }))}
              >
                <option value="transferencia">Transferencia</option>
                <option value="efectivo">Efectivo</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-semibold text-slate-700">Referencia / CLABE</label>
              <input
                className="block mt-1 w-full border rounded px-2 py-1.5 text-sm"
                value={editForm.payment_reference}
                onChange={e => setEditForm(f => ({ ...f, payment_reference: e.target.value }))}
                placeholder="CLABE o numero de operacion"
              />
            </div>
          </div>

          {/* Motivo de edicion (obligatorio) */}
          <div>
            <label className="text-sm font-semibold text-slate-700">
              Motivo de edicion <span className="text-red-500">*</span> <span className="text-slate-400 font-normal">(min. 10 caracteres)</span>
            </label>
            <textarea
              className="block mt-1 w-full border rounded px-2 py-1.5 text-sm resize-none"
              rows={3}
              placeholder="Describe el motivo de la correccion manual..."
              value={editForm.edit_notes}
              onChange={e => setEditForm(f => ({ ...f, edit_notes: e.target.value }))}
              data-testid="edit-notes-input"
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setEditing(false)}>Cancelar</Button>
            <Button
              className="bg-[#FFDF00] text-slate-900 hover:bg-yellow-400 font-semibold"
              onClick={handleSave}
              disabled={saving}
              data-testid="btn-save-edit"
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </div>
        </div>
      )}

      {run.manually_edited && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
          Editado manualmente por {run.edited_by_user_id} · {run.edited_at?.slice(0, 19)} · Motivo: {run.edit_notes}
        </div>
      )}
    </div>
  );
}
